import tinycolor from "tinycolor2";
import type { ColorToken, ColorGroup } from "../types";

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export interface ColorValidationResult extends ValidationResult {
  type: "success" | "error" | "warning";
  message?: string;
}

export interface DuplicateCheckResult {
  exists: boolean;
  location: string;
  existingColor?: ColorToken;
}

export interface GroupConflictResult {
  willMerge: boolean;
  existingGroup?: ColorGroup;
  message?: string;
}

/**
 * Validates color names for security and format compliance
 */
export const validateColorName = (name: string): ValidationResult => {
  const trimmed = name.trim();

  if (!trimmed) {
    return { valid: false, error: "Color name cannot be empty" };
  }

  if (trimmed.length > 50) {
    return { valid: false, error: "Color name too long (max 50 characters)" };
  }

  // Prevent XSS and ensure valid identifier characters
  // Allow: letters, numbers, dash, underscore, slash (for groups)
  if (!/^[a-zA-Z0-9\-_\/]+$/.test(trimmed)) {
    return {
      valid: false,
      error:
        "Invalid characters. Use only letters, numbers, dash, underscore, and slash",
    };
  }

  // Don't allow names starting or ending with special characters
  if (/^[-_\/]|[-_\/]$/.test(trimmed)) {
    return {
      valid: false,
      error: "Name cannot start or end with dash, underscore, or slash",
    };
  }

  // Don't allow consecutive slashes or multiple slashes
  if (/\/\//.test(trimmed) || (trimmed.match(/\//g) || []).length > 1) {
    return {
      valid: false,
      error: "Use only one slash to separate group and color name",
    };
  }

  // If it contains a slash, validate both parts
  if (trimmed.includes("/")) {
    const parts = trimmed.split("/");
    if (parts.length !== 2) {
      return {
        valid: false,
        error: "Use format: group/color-name",
      };
    }

    const [groupName, colorName] = parts;
    if (!groupName.trim() || !colorName.trim()) {
      return {
        valid: false,
        error: "Both group and color name must be provided",
      };
    }

    // Validate each part recursively (without slash)
    const groupValidation = validateColorName(groupName);
    if (!groupValidation.valid) {
      return {
        valid: false,
        error: `Group name: ${groupValidation.error}`,
      };
    }

    const colorValidation = validateColorName(colorName);
    if (!colorValidation.valid) {
      return {
        valid: false,
        error: `Color name: ${colorValidation.error}`,
      };
    }
  }

  return { valid: true };
};

/**
 * Validates color values including hex, CSS functions, and custom properties
 */
export const validateColorValue = (value: string): ValidationResult => {
  const trimmed = value.trim();

  if (!trimmed) {
    return { valid: false, error: "Color value cannot be empty" };
  }

  // Allow CSS custom properties (var(--...))
  if (trimmed.includes("var(--")) {
    // Basic validation for CSS custom property syntax
    // Allow formats like: var(--primary-color), var(--my-custom-color), etc.
    if (!/var\(--[a-zA-Z0-9\-_]+\)/.test(trimmed)) {
      return {
        valid: false,
        error: "Invalid CSS custom property format",
      };
    }
    // Ensure balanced parentheses
    const openParens = (trimmed.match(/\(/g) || []).length;
    const closeParens = (trimmed.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      return {
        valid: false,
        error: "Unbalanced parentheses in CSS variable",
      };
    }
    return { valid: true };
  }

  // Allow CSS color functions (hsl, rgb, oklch, etc.)
  if (/^(hsl|rgb|hwb|oklch|lab|lch)\s*\(/.test(trimmed)) {
    // Basic syntax validation - ensure balanced parentheses
    const openParens = (trimmed.match(/\(/g) || []).length;
    const closeParens = (trimmed.match(/\)/g) || []).length;
    if (openParens !== closeParens) {
      return {
        valid: false,
        error: "Unbalanced parentheses in color function",
      };
    }
    return { valid: true };
  }

  // Validate with tinycolor for standard color formats
  const color = tinycolor(trimmed);
  if (!color.isValid()) {
    return {
      valid: false,
      error:
        "Invalid color format. Use hex (#ff0000), rgb(), hsl(), or CSS custom properties",
    };
  }

  return { valid: true };
};

/**
 * Validates group names with similar rules to color names but no slash allowed
 */
export const validateGroupName = (name: string): ValidationResult => {
  const trimmed = name.trim();

  if (!trimmed) {
    return { valid: false, error: "Group name cannot be empty" };
  }

  if (trimmed.length > 30) {
    return { valid: false, error: "Group name too long (max 30 characters)" };
  }

  // No slashes allowed in group names
  if (trimmed.includes("/")) {
    return {
      valid: false,
      error: "Group names cannot contain slashes",
    };
  }

  // Use the same character validation as color names
  if (!/^[a-zA-Z0-9\-_]+$/.test(trimmed)) {
    return {
      valid: false,
      error:
        "Invalid characters. Use only letters, numbers, dash, and underscore",
    };
  }

  // Don't allow names starting or ending with special characters
  if (/^[-_]|[-_]$/.test(trimmed)) {
    return {
      valid: false,
      error: "Name cannot start or end with dash or underscore",
    };
  }

  return { valid: true };
};

/**
 * Check if a color already exists in the design tokens
 */
export const checkColorExists = (
  colorName: string,
  groupName: string | null,
  colorGroups: ColorGroup[],
  ungroupedColors: ColorToken[],
  excludeColorId?: string, // For edit mode, exclude the current color being edited (format: "group/color" or "color")
): DuplicateCheckResult => {
  // Helper function to check if a color should be excluded (is the current color being edited)
  const shouldExcludeColor = (
    color: ColorToken,
    group: string | null,
  ): boolean => {
    if (!excludeColorId) return false;

    // For grouped colors, check if the path matches the excludeColorId
    if (group && color.group === group) {
      const colorPath = `${group}/${color.name}`;
      return colorPath === excludeColorId;
    }

    // For ungrouped colors, check if the name matches the excludeColorId
    if (!group && !color.group) {
      return color.name === excludeColorId;
    }

    return false;
  };

  // Check ungrouped colors
  if (!groupName || groupName === colorName) {
    const existingUngrouped = ungroupedColors.find(
      (c) => c.name === colorName && !shouldExcludeColor(c, null),
    );
    if (existingUngrouped) {
      return {
        exists: true,
        location: "ungrouped colors",
        existingColor: existingUngrouped,
      };
    }
  }

  // Check grouped colors
  if (groupName && groupName !== colorName) {
    const targetGroup = colorGroups.find((g) => g.name === groupName);
    if (targetGroup) {
      const existingInGroup = targetGroup.colors.find(
        (c) => c.name === colorName && !shouldExcludeColor(c, groupName),
      );
      if (existingInGroup) {
        return {
          exists: true,
          location: `group "${groupName}"`,
          existingColor: existingInGroup,
        };
      }
    }
  }

  return { exists: false, location: "" };
};

/**
 * Check if creating an ungrouped color would merge with an existing group
 */
export const checkUngroupedMergeConflict = (
  colorName: string,
  colorGroups: ColorGroup[],
): GroupConflictResult => {
  const existingGroup = colorGroups.find((g) => g.name === colorName);
  if (existingGroup && existingGroup.colors.length > 0) {
    return {
      willMerge: true,
      existingGroup,
      message: `Creating "${colorName}" will merge with existing group "${colorName}"`,
    };
  }
  return { willMerge: false };
};

/**
 * Check if creating a new group would conflict with existing ungrouped colors
 */
export const checkGroupCreationConflict = (
  groupName: string,
  ungroupedColors: ColorToken[],
): GroupConflictResult => {
  const existingUngrouped = ungroupedColors.find((c) => c.name === groupName);
  if (existingUngrouped) {
    return {
      willMerge: true,
      message: `Creating group "${groupName}" will move existing color "${groupName}" to "${groupName}/default"`,
    };
  }
  return { willMerge: false };
};

/**
 * Comprehensive validation for color names with instant feedback
 */
export const validateColorNameWithFeedback = (
  name: string,
  group: string | null,
  colorGroups: ColorGroup[],
  ungroupedColors: ColorToken[],
  isEditMode = false,
  selectedColor?: ColorToken,
): ColorValidationResult => {
  const trimmed = name.trim().toLowerCase();
  const excludeColorId = selectedColor
    ? `${selectedColor.group}/${selectedColor.name}`
    : undefined;

  if (selectedColor?.name === name && selectedColor?.group === group) {
    return {
      valid: true,
      type: "success",
    };
  }

  // Basic validation
  const basicValidation = validateColorName(trimmed);
  if (!basicValidation.valid) {
    return {
      valid: false,
      type: "error",
      error: basicValidation.error,
      message: basicValidation.error,
    };
  }

  if (!trimmed) {
    return {
      valid: false,
      type: "error",
      error: "Color name cannot be empty",
      message: "Color name cannot be empty",
    };
  }

  // Parse group and color name
  const parts = trimmed.split("/");
  const isGrouped = parts.length === 2;
  const groupName = isGrouped ? parts[0].trim() : group;
  const colorName = isGrouped ? parts[1].trim() : trimmed;

  if (colorName === groupName) {
    return {
      valid: false,
      type: "error",
      message: "Color name cannot be the same as the group name",
    };
  }

  if (colorGroups.find((g) => g.name === colorName)) {
    return {
      valid: false,
      type: "error",
      message: `Group "${colorName}" already exists`,
    };
  }

  if (selectedColor?.group && isGrouped) {
    return {
      valid: false,
      type: "error",
      message: "Cannot create another group within a group",
    };
  }

  // Check for duplicates
  const duplicateCheck = checkColorExists(
    colorName,
    groupName,
    colorGroups,
    ungroupedColors,
    excludeColorId,
  );

  if (duplicateCheck.exists) {
    return {
      valid: false,
      type: "error",
      message: `Color "${colorName}" already exists in ${duplicateCheck.location}`,
    };
  }

  // Check for ungrouped merge conflicts (when creating ungrouped color that matches group name)
  if (!isGrouped && !isEditMode) {
    const mergeConflict = checkUngroupedMergeConflict(colorName, colorGroups);
    if (mergeConflict.willMerge) {
      return {
        valid: true,
        type: "warning",
        message: mergeConflict.message,
      };
    }
  }

  // Check for group creation conflicts (when creating group that matches ungrouped color)
  if (isGrouped && !isEditMode && groupName) {
    const groupConflict = checkGroupCreationConflict(
      groupName,
      ungroupedColors,
    );
    if (groupConflict.willMerge) {
      return {
        valid: true,
        type: "warning",
        message: groupConflict.message,
      };
    }
  }

  // Success cases with appropriate messages
  if (isGrouped) {
    return {
      valid: true,
      type: "success",
      message: `Will create "${colorName}" in group "${groupName}"`,
    };
  }

  return {
    valid: true,
    type: "success",
  };
};

/**
 * Helper function to handle validation with consistent error reporting
 */
export const validateAndReport = (
  validation: ValidationResult,
  errorType: string,
  setError: (error: string | null) => void,
  showToast: (message: string, description?: string) => void,
): boolean => {
  if (!validation.valid) {
    setError(`${errorType}: ${validation.error}`);
    showToast(`Invalid ${errorType.toLowerCase()}`, validation.error);
    return false;
  }
  return true;
};

/**
 * Sanitizes input by removing potentially harmful characters
 */
export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>\"'&]/g, "") // Remove common XSS characters
    .slice(0, 50); // Limit length
};
